import React, { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PageTitle from "../../layouts/PageTitle";
import { useGetLabourDetailLedgerQuery } from "../../../store/appApi";
import { selectHasModuleAccess } from "../../../store/authSlice";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const QUARTER_MAP = {
  Q1: [0, 1, 2],
  Q2: [3, 4, 5],
  Q3: [6, 7, 8],
  Q4: [9, 10, 11],
};
const YEAR_OPTIONS = [2023, 2024, 2025, 2026, 2027];

// ─────────────────────────────────────────────────────────────────────────────
// Mock time-series — individual labourer payment history (₹)
// ─────────────────────────────────────────────────────────────────────────────
const DETAIL_MOCK = [
  { date: "2024-01-08", amount: 8200 },
  { date: "2024-01-22", amount: 7400 },
  { date: "2024-02-10", amount: 9100 },
  { date: "2024-02-25", amount: 6800 },
  { date: "2024-03-06", amount: 10500 },
  { date: "2024-03-20", amount: 8700 },
  { date: "2024-04-11", amount: 9800 },
  { date: "2024-04-27", amount: 8100 },
  { date: "2024-05-09", amount: 11200 },
  { date: "2024-05-23", amount: 9400 },
  { date: "2024-06-12", amount: 12800 },
  { date: "2024-06-26", amount: 10600 },
  { date: "2024-07-08", amount: 13500 },
  { date: "2024-07-22", amount: 11800 },
  { date: "2024-08-05", amount: 14200 },
  { date: "2024-08-19", amount: 12400 },
  { date: "2024-09-10", amount: 13100 },
  { date: "2024-09-24", amount: 11200 },
  { date: "2024-10-07", amount: 12000 },
  { date: "2024-10-21", amount: 10300 },
  { date: "2024-11-04", amount: 13800 },
  { date: "2024-11-18", amount: 11900 },
  { date: "2024-12-02", amount: 15600 },
  { date: "2024-12-16", amount: 13200 },
  { date: "2025-01-09", amount: 9100 },
  { date: "2025-01-27", amount: 7800 },
  { date: "2025-02-13", amount: 10200 },
  { date: "2025-02-27", amount: 7200 },
  { date: "2025-03-10", amount: 11800 },
  { date: "2025-03-24", amount: 9600 },
  { date: "2025-04-08", amount: 10900 },
  { date: "2025-04-22", amount: 9200 },
  { date: "2025-05-06", amount: 12400 },
  { date: "2025-05-20", amount: 10500 },
  { date: "2025-06-10", amount: 14100 },
  { date: "2025-06-24", amount: 11800 },
  { date: "2025-07-07", amount: 15000 },
  { date: "2025-07-21", amount: 12900 },
  { date: "2025-08-04", amount: 15800 },
  { date: "2025-08-18", amount: 13500 },
  { date: "2025-09-09", amount: 14600 },
  { date: "2025-09-23", amount: 12700 },
  { date: "2025-10-06", amount: 13400 },
  { date: "2025-10-20", amount: 11600 },
  { date: "2025-11-03", amount: 15200 },
  { date: "2025-11-17", amount: 13000 },
  { date: "2025-12-01", amount: 17400 },
  { date: "2025-12-15", amount: 14800 },
  { date: "2026-01-08", amount: 9800 },
  { date: "2026-01-26", amount: 8500 },
  { date: "2026-02-12", amount: 11000 },
  { date: "2026-02-26", amount: 7900 },
  { date: "2026-03-11", amount: 12700 },
  { date: "2026-03-25", amount: 10200 },
  { date: "2026-04-09", amount: 11800 },
  { date: "2026-04-23", amount: 10000 },
  { date: "2026-05-07", amount: 13500 },
  { date: "2026-05-21", amount: 11400 },
  { date: "2026-06-11", amount: 15200 },
  { date: "2026-06-26", amount: 12600 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const sumArr = (arr) => arr.reduce((a, b) => a + b, 0);
const weekBucket = (day) => (day <= 7 ? 0 : day <= 14 ? 1 : day <= 21 ? 2 : 3);

// ─────────────────────────────────────────────────────────────────────────────
// Shared custom tooltip
// ─────────────────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 6,
        padding: "10px 14px",
        fontSize: 12,
      }}
    >
      <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#495057" }}>
        {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ margin: "2px 0", color: entry.color }}>
          {entry.name}: <strong>{fmtINR(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────────
const NoData = ({ year }) => (
  <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
    <svg
      width="52"
      height="52"
      viewBox="0 0 52 52"
      fill="none"
      style={{ opacity: 0.35, marginBottom: 12 }}
    >
      <circle cx="26" cy="26" r="25" stroke="#6c757d" strokeWidth="2" />
      <path
        d="M16 36 L26 16 L36 36"
        stroke="#6c757d"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="26" cy="40" r="2" fill="#6c757d" />
    </svg>
    <p className="mb-0 small">
      No expense data recorded for <strong>{year}</strong>
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Payment calendar — shows which days in a month had payments
// ─────────────────────────────────────────────────────────────────────────────
const PAY_COLOR   = "#7B2FBE";
const ADV_COLOR   = "#f6a821";
const BOTH_COLOR  = "#3f9ae0";

const LabourPaymentCalendar = ({ transactions, loading, labourName }) => {
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  // Build a map: YYYY-MM-DD → { payments: [], receipts: [] }
  const dayMap = useMemo(() => {
    const map = {};
    (transactions || []).forEach((txn) => {
      if (!txn.created) return;
      const d = new Date(txn.created);
      const key = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getDate()).padStart(2, "0"),
      ].join("-");
      if (!map[key]) map[key] = { payments: [], receipts: [] };
      if (txn.type === "payment") map[key].payments.push(txn);
      else map[key].receipts.push(txn);
    });
    return map;
  }, [transactions]);

  const year  = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay    = new Date(year, month, 1).getDay();
  const blanks      = Array(firstDay).fill(null);
  const calDays     = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return { d, key, dow: new Date(year, month, d).getDay() };
  });

  const sel = selectedDay ? dayMap[selectedDay] : null;

  const prevMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() - 1); setMonthDate(d); setSelectedDay(null); };
  const nextMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() + 1); setMonthDate(d); setSelectedDay(null); };

  // Summary for the visible month
  const { payDays, totalPaid, totalAdvance } = useMemo(() => {
    let days = 0, paid = 0, adv = 0;
    calDays.forEach(({ key }) => {
      const e = dayMap[key];
      if (!e) return;
      days++;
      e.payments.forEach((t) => { paid += Number(t.amount) || 0; });
      e.receipts.forEach((t) => { adv  += Number(t.amount) || 0; });
    });
    return { payDays: days, totalPaid: paid, totalAdvance: adv };
  }, [calDays, dayMap]);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h5 className="card-title mb-0">Payment Calendar{labourName ? ` — ${labourName}` : ""}</h5>
          <small className="text-muted">Days with payment activity this month</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={prevMonth}>&lsaquo;</button>
          <span className="fw-semibold" style={{ minWidth: 130, textAlign: "center" }}>
            {monthDate.toLocaleString("default", { month: "long", year: "numeric" })}
          </span>
          <button className="btn btn-sm btn-outline-secondary" onClick={nextMonth}>&rsaquo;</button>
        </div>
      </div>

      {/* Month summary strip */}
      <div className="d-flex border-bottom">
        <div className="flex-fill text-center py-2" style={{ borderLeft: `3px solid ${PAY_COLOR}` }}>
          <div className="fw-bold fs-5">{payDays}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>Pay Days</div>
        </div>
        <div className="flex-fill text-center py-2" style={{ borderLeft: `3px solid ${PAY_COLOR}` }}>
          <div className="fw-bold fs-5">{fmtINR(totalPaid)}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>Paid</div>
        </div>
        <div className="flex-fill text-center py-2" style={{ borderLeft: `3px solid ${ADV_COLOR}` }}>
          <div className="fw-bold fs-5">{fmtINR(totalAdvance)}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>Advance</div>
        </div>
      </div>

      <div className="card-body p-2">
        {loading ? (
          <div className="text-center py-4">Loading…</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="text-center fw-bold text-muted py-1" style={{ fontSize: 10 }}>{d}</div>
            ))}
            {blanks.map((_, i) => <div key={`b${i}`} />)}
            {calDays.map(({ d, key, dow }) => {
              const entry   = dayMap[key];
              const hasPay  = entry?.payments?.length > 0;
              const hasAdv  = entry?.receipts?.length > 0;
              const isWeekend = dow === 0 || dow === 6;
              const color   = hasPay && hasAdv ? BOTH_COLOR : hasPay ? PAY_COLOR : hasAdv ? ADV_COLOR : null;

              return (
                <div
                  key={key}
                  onClick={() => entry && setSelectedDay(key === selectedDay ? null : key)}
                  style={{
                    background: color ? `${color}22` : isWeekend ? "#f5f5f5" : "#fafafa",
                    borderLeft: `3px solid ${color || (isWeekend ? "#dee2e6" : "#e9ecef")}`,
                    borderRadius: 4,
                    padding: "4px 3px",
                    cursor: entry ? "pointer" : "default",
                    outline: selectedDay === key ? `2px solid ${color}` : "none",
                    minHeight: 44,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 12, color: color || (isWeekend ? "#aaa" : "#555") }}>{d}</div>
                  {color && (
                    <div style={{ fontSize: 8, fontWeight: 600, color, textAlign: "center", lineHeight: 1.1 }}>
                      {hasPay && hasAdv ? "Pay+Adv" : hasPay ? "Paid" : "Adv"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected day detail */}
      {sel && selectedDay && (
        <div className="card-footer" style={{ background: "#f8f9fa" }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold">{selectedDay}</span>
            <button className="btn btn-xs btn-outline-secondary py-0 px-1" onClick={() => setSelectedDay(null)}>✕</button>
          </div>
          {sel.payments.length > 0 && (
            <div className="mb-1" style={{ fontSize: 13 }}>
              <strong style={{ color: PAY_COLOR }}>Payments:</strong>{" "}
              {sel.payments.map((t, i) => (
                <span key={i} className="me-2">{fmtINR(t.amount)}{t.desc ? ` (${t.desc})` : ""}</span>
              ))}
            </div>
          )}
          {sel.receipts.length > 0 && (
            <div style={{ fontSize: 13 }}>
              <strong style={{ color: ADV_COLOR }}>Advance:</strong>{" "}
              {sel.receipts.map((t, i) => (
                <span key={i} className="me-2">{fmtINR(t.amount)}{t.desc ? ` (${t.desc})` : ""}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="card-footer d-flex gap-3 py-2 flex-wrap" style={{ fontSize: 11 }}>
        <span className="d-flex align-items-center gap-1">
          <span style={{ width: 10, height: 10, background: PAY_COLOR, borderRadius: 2, display: "inline-block" }} /> Payment
        </span>
        <span className="d-flex align-items-center gap-1">
          <span style={{ width: 10, height: 10, background: ADV_COLOR, borderRadius: 2, display: "inline-block" }} /> Advance
        </span>
        <span className="d-flex align-items-center gap-1">
          <span style={{ width: 10, height: 10, background: BOTH_COLOR, borderRadius: 2, display: "inline-block" }} /> Payment + Advance
        </span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const LabourDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const hasAccess = useSelector(selectHasModuleAccess("labour", "read"));

  // ── Existing API ───────────────────────────────────────────────────────────
  const { data, isLoading, isFetching, isError } =
    useGetLabourDetailLedgerQuery({ id, page }, { skip: !hasAccess });

  const labour = data?.labour ?? {};
  const transactions = Array.isArray(data?.transactions)
    ? data.transactions
    : [];
  const grandTotal = Number(data?.grandTotal) || 0;
  const total = Number(data?.total) || 0;
  const loading = isLoading || isFetching;

  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fmt = (n) => Number(n).toLocaleString("en-IN");
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  // ── Chart 1 — comparison (YoY / QoQ / MoM) ────────────────────────────────
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [chartQuarter, setChartQuarter] = useState("all");
  const [chartMonth, setChartMonth] = useState("all");
  const [comparisonMode, setComparisonMode] = useState("YoY");

  const { chartData, currentLabel, priorLabel, currentTotal, priorTotal } =
    useMemo(() => {
      const yr = chartYear;
      const prevYr = yr - 1;

      if (comparisonMode === "YoY") {
        const currB = Array(12).fill(0);
        const priorB = Array(12).fill(0);
        DETAIL_MOCK.forEach(({ date, amount }) => {
          const d = new Date(date);
          const m = d.getMonth();
          if (d.getFullYear() === yr) currB[m] += amount;
          else if (d.getFullYear() === prevYr) priorB[m] += amount;
        });
        return {
          chartData: MONTHS.map((label, i) => ({
            label,
            current: currB[i],
            prior: priorB[i],
          })),
          currentLabel: String(yr),
          priorLabel: String(prevYr),
          currentTotal: sumArr(currB),
          priorTotal: sumArr(priorB),
        };
      }

      if (comparisonMode === "QoQ") {
        const qKey =
          chartQuarter === "all"
            ? `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`
            : chartQuarter;
        const qNum = parseInt(qKey[1], 10);
        const prevQKey = qNum === 1 ? "Q4" : `Q${qNum - 1}`;
        const prevQYear = qNum === 1 ? yr - 1 : yr;
        const currIdxs = QUARTER_MAP[qKey];
        const prevIdxs = QUARTER_MAP[prevQKey];
        const currB = Array(3).fill(0);
        const priorB = Array(3).fill(0);
        DETAIL_MOCK.forEach(({ date, amount }) => {
          const d = new Date(date);
          const y = d.getFullYear();
          const m = d.getMonth();
          const ci = currIdxs.indexOf(m);
          const pi = prevIdxs.indexOf(m);
          if (y === yr && ci !== -1) currB[ci] += amount;
          if (y === prevQYear && pi !== -1) priorB[pi] += amount;
        });
        return {
          chartData: currIdxs.map((mi, pos) => ({
            label: MONTHS[mi],
            current: currB[pos],
            prior: priorB[pos],
          })),
          currentLabel: `${qKey} ${yr}`,
          priorLabel: `${prevQKey} ${prevQYear}`,
          currentTotal: sumArr(currB),
          priorTotal: sumArr(priorB),
        };
      }

      // MoM
      const mIdx =
        chartMonth === "all"
          ? new Date().getMonth()
          : parseInt(chartMonth, 10) - 1;
      const prevMIdx = mIdx === 0 ? 11 : mIdx - 1;
      const prevMYear = mIdx === 0 ? yr - 1 : yr;
      const currB = Array(4).fill(0);
      const priorB = Array(4).fill(0);
      DETAIL_MOCK.forEach(({ date, amount }) => {
        const d = new Date(date);
        const y = d.getFullYear();
        const m = d.getMonth();
        const w = weekBucket(d.getDate());
        if (y === yr && m === mIdx) currB[w] += amount;
        if (y === prevMYear && m === prevMIdx) priorB[w] += amount;
      });
      const WEEK_LABELS = ["Week 1", "Week 2", "Week 3", "Week 4"];
      return {
        chartData: WEEK_LABELS.map((label, i) => ({
          label,
          current: currB[i],
          prior: priorB[i],
        })),
        currentLabel: `${MONTHS[mIdx]} ${yr}`,
        priorLabel: `${MONTHS[prevMIdx]} ${prevMYear}`,
        currentTotal: sumArr(currB),
        priorTotal: sumArr(priorB),
      };
    }, [chartYear, chartQuarter, chartMonth, comparisonMode]);

  const rawChange =
    priorTotal > 0
      ? (((currentTotal - priorTotal) / priorTotal) * 100).toFixed(1)
      : null;
  const changeUp = rawChange !== null && Number(rawChange) >= 0;

  // ── Chart 2 — monthly expense trend (area) ─────────────────────────────────
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());

  const { trendData, trendTotal, trendHasData } = useMemo(() => {
    const buckets = Array(12).fill(0);
    DETAIL_MOCK.forEach(({ date, amount }) => {
      const d = new Date(date);
      if (d.getFullYear() === trendYear) buckets[d.getMonth()] += amount;
    });
    const hasData = buckets.some((v) => v > 0);

    // Find the last month that actually has data so we don't render
    // empty axis ticks for future / unrecorded months.
    const lastFilledIdx = buckets.reduce(
      (last, v, i) => (v > 0 ? i : last),
      -1,
    );
    // For complete years show all 12 months; for partial years stop after
    // the last month with data (+1 empty slot so the area doesn't end abruptly).
    const visibleCount =
      lastFilledIdx === 11 ? 12 : Math.min(lastFilledIdx + 2, 12);
    const slicedMonths = hasData ? MONTHS.slice(0, visibleCount) : MONTHS;

    return {
      trendData: slicedMonths.map((label, i) => ({
        label,
        expense: buckets[i] || null,
      })),
      trendTotal: sumArr(buckets),
      trendHasData: hasData,
    };
  }, [trendYear]);

  // ── Access guard ──────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <>
        <PageTitle activeMenu="Labour Detail" motherMenu="Labour" />
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
                <h5>Access Restricted</h5>
                {/* <p className="text-muted mb-0">
                  Labour payment details are only available to{" "}
                  <strong>Super Admin</strong> and <strong>Auditor</strong>{" "}
                  roles.
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Labour Detail" motherMenu="Labour" />

      <div className="row">
        <div className="col-lg-6 mx-auto d-flex flex-column">
          {/* ══════════════════════════════════════════════════════════════════
              CHART 1 — Comparison (YoY / QoQ / MoM)
          ══════════════════════════════════════════════════════════════════ */}
          <div className="card mb-4 h-100">
            {/* Filter row */}
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{ background: "#fff", borderBottom: "1px solid #f0f2f5" }}
            >
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={chartYear}
                  onChange={(e) => setChartYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Quarter
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 132 }}
                  value={chartQuarter}
                  onChange={(e) => setChartQuarter(e.target.value)}
                  disabled={comparisonMode === "MoM"}
                >
                  <option value="all">All Quarters</option>
                  <option value="Q1">Q1 (Jan–Mar)</option>
                  <option value="Q2">Q2 (Apr–Jun)</option>
                  <option value="Q3">Q3 (Jul–Sep)</option>
                  <option value="Q4">Q4 (Oct–Dec)</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Month
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 120 }}
                  value={chartMonth}
                  onChange={(e) => setChartMonth(e.target.value)}
                  disabled={comparisonMode !== "MoM"}
                >
                  <option value="all">All Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={String(i + 1)}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ms-auto">
                <div className="btn-group" role="group">
                  {["YoY", "QoQ", "MoM"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`btn btn-sm ${comparisonMode === mode ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setComparisonMode(mode)}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* KPI strip */}
            <div
              className="d-flex flex-wrap gap-0"
              style={{ borderBottom: "1px solid #f0f2f5" }}
            >
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: "4px solid #7B2FBE",
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Current · {currentLabel}
                </span>
                <span className="fs-5 fw-bold" style={{ color: "#7B2FBE" }}>
                  {fmtINR(currentTotal)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: "4px solid #F5A623",
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Prior · {priorLabel}
                </span>
                <span className="fs-5 fw-bold" style={{ color: "#F5A623" }}>
                  {fmtINR(priorTotal)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderLeft: `4px solid ${rawChange === null ? "#adb5bd" : changeUp ? "#dc3545" : "#198754"}`,
                }}
              >
                <span className="d-block text-muted small mb-1">Change</span>
                <span
                  className="fs-5 fw-bold"
                  style={{
                    color:
                      rawChange === null
                        ? "#adb5bd"
                        : changeUp
                          ? "#dc3545"
                          : "#198754",
                  }}
                >
                  {rawChange === null
                    ? "—"
                    : `${changeUp ? "▲" : "▼"} ${Math.abs(Number(rawChange))}%`}
                </span>
              </div>
            </div>

            {/* Bar chart */}
            <div className="card-body px-3 pt-3 pb-2">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h6 className="mb-0 fw-semibold">
                    Payment Distribution Trend
                    {labour.name ? ` — ${labour.name}` : ""}
                  </h6>
                  <small className="text-muted">
                    {comparisonMode === "YoY" &&
                      "Year-over-Year · Monthly breakdown"}
                    {comparisonMode === "QoQ" &&
                      "Quarter-over-Quarter · Monthly breakdown within quarter"}
                    {comparisonMode === "MoM" &&
                      "Month-over-Month · Weekly breakdown"}
                  </small>
                </div>
                <span
                  className="badge fw-semibold px-3 py-2"
                  style={{
                    background: "#f3eafd",
                    color: "#7B2FBE",
                    fontSize: 12,
                  }}
                >
                  {comparisonMode}
                </span>
              </div>

              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                  barCategoryGap="28%"
                  barGap={4}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f2f5"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={fmtINR}
                    tick={{ fontSize: 11, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                    width={80}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "#f8f9fa" }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                    iconType="circle"
                    iconSize={10}
                  />
                  <Bar
                    dataKey="current"
                    name={currentLabel}
                    fill="#7B2FBE"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                  <Bar
                    dataKey="prior"
                    name={priorLabel}
                    fill="#F5A623"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="col-lg-6 mx-auto d-flex flex-column">
          {/* ══════════════════════════════════════════════════════════════════
              CHART 2 — Monthly Expense Trend (area)
          ══════════════════════════════════════════════════════════════════ */}
          <div className="card mb-4 h-100 d-flex flex-column">
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{ background: "#fff", borderBottom: "1px solid #f0f2f5" }}
            >
              <div>
                <h6 className="mb-0 fw-semibold">Monthly Expense Trend</h6>
                <small className="text-muted">
                  Individual payment spend · month by month
                </small>
              </div>

              <div className="d-flex align-items-center gap-2 ms-auto">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={trendYear}
                  onChange={(e) => setTrendYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {trendHasData && (
                <span
                  className="badge fw-semibold px-3 py-2"
                  style={{
                    background: "#f3eafd",
                    color: "#7B2FBE",
                    fontSize: 12,
                  }}
                >
                  Total {fmtINR(trendTotal)}
                </span>
              )}
            </div>

            <div className="card-body px-3 pt-3 pb-2 flex-grow-1 d-flex flex-column">
              {!trendHasData ? (
                <NoData year={trendYear} />
              ) : (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={trendData}
                      margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                    >
                      <defs>
                        <linearGradient
                          id="detailTrendGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#7B2FBE"
                            stopOpacity={0.25}
                          />
                          <stop
                            offset="95%"
                            stopColor="#7B2FBE"
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f0f2f5"
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: "#6c757d" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={fmtINR}
                        tick={{ fontSize: 11, fill: "#6c757d" }}
                        axisLine={false}
                        tickLine={false}
                        width={80}
                      />
                      <Tooltip
                        content={<ChartTooltip />}
                        cursor={{
                          stroke: "#7B2FBE",
                          strokeWidth: 1,
                          strokeDasharray: "4 4",
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                        iconType="circle"
                        iconSize={10}
                      />
                      <Area
                        type="monotone"
                        dataKey="expense"
                        name={`Expense ${trendYear}`}
                        stroke="#7B2FBE"
                        strokeWidth={2.5}
                        fill="url(#detailTrendGrad)"
                        dot={{ r: 4, fill: "#7B2FBE", strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: "#7B2FBE" }}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* ══════════════════════════════════════════════════════════════════
              PAYMENT CALENDAR — month view of payment days
          ══════════════════════════════════════════════════════════════════ */}
        <LabourPaymentCalendar transactions={transactions} loading={loading} labourName={labour.name} />

        {/* ══════════════════════════════════════════════════════════════════
              EXISTING DATA — labourer meta + ledger table + pagination (untouched)
          ══════════════════════════════════════════════════════════════════ */}
        <div
          className="card mb-3"
          style={{ background: "#f8f9fa", height: "120px" }}
        >
          <div className="card-body d-flex flex-wrap align-items-center gap-4">
            <div>
              <span
                className="text-muted small d-block"
                style={{ fontSize: "1rem" }}
              >
                Labourer
              </span>
              <span className="fw-bold fs-5">
                {loading ? "…" : labour.name || "—"}
              </span>
              {labour.laborCat && (
                <span
                  className="text-muted small d-block"
                  style={{ fontSize: "1rem", textTransform: "capitalize" }}
                >
                  {labour.laborCat}
                  {labour.typeOfLabour
                    ? ` · ${labour.typeOfLabour} @ ₹${labour.rate || 0}`
                    : ""}
                </span>
              )}
            </div>
            <div className="ms-auto text-end">
              <span className="text-muted small d-block">Grand Total Paid</span>
              <span className="fw-bold fs-5 text-danger">
                ₹ {loading ? "…" : fmt(grandTotal)}
              </span>
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Payment Ledger</h5>
            <span className="badge badge-primary ms-2">{total}</span>
          </div>

          <div className="card-body">
            {isError && (
              <div className="alert alert-danger">
                Failed to load labour transactions. Please try again.
              </div>
            )}

            <div className="table-responsive">
              <table className="display dataTable no-footer w-100">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Amount (₹)</th>
                    <th>Overtime (₹)</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading && transactions.length === 0 && !isError && (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        No payment transactions found for this labourer.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    transactions.map((txn, idx) => {
                      const isReceipt = txn.type === "receipt";
                      return (
                        <tr key={txn._id || idx}>
                          <td className="text-muted small">
                            {(page - 1) * PAGE_SIZE + idx + 1}
                          </td>
                          <td>{fmtDate(txn.created)}</td>
                          <td>
                            <span
                              className={
                                isReceipt
                                  ? "badge badge-success"
                                  : "badge badge-danger"
                              }
                              style={{ textTransform: "capitalize" }}
                            >
                              {txn.type}
                            </span>
                          </td>
                          <td
                            className={`fw-semibold ${isReceipt ? "text-success" : "text-danger"}`}
                          >
                            ₹ {fmt(txn.amount)}
                          </td>
                          <td className="text-muted small">
                            {txn.overtime ? `₹ ${fmt(txn.overtime)}` : "—"}
                          </td>
                          <td className="text-muted small">
                            {txn.desc || "—"}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {!loading && totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted small">
                  Page {page} of {totalPages}
                </span>
                <div className="dataTables_paginate paging_simple_numbers">
                  <Link
                    to="#"
                    className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                    onClick={() => page > 1 && setPage(page - 1)}
                  >
                    Previous
                  </Link>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (n) => (
                      <Link
                        key={n}
                        to="#"
                        className={`paginate_button ${page === n ? "current" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </Link>
                    ),
                  )}
                  <Link
                    to="#"
                    className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                    onClick={() => page < totalPages && setPage(page + 1)}
                  >
                    Next
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LabourDetail;
